/**
 * General MIDI instrument table.
 *
 * Source of truth: MIDI.js soundfonts naming convention (FluidR3_GM bank).
 * Cf. https://gleitz.github.io/midi-js-soundfonts/FluidR3_GM/names.json
 *
 * Names are kebab-case with underscores as used in MIDI.js sample URLs:
 * `${baseUrl}/${GM_INSTRUMENT_NAMES[program]}-mp3/${note}.mp3`.
 *
 * The table is indexed 0..127 (GM program number). Index is stable; do NOT
 * reorder. The companion `GM_DRUMS_NAME` is the bank name for MIDI channel 10
 * (drum kit).
 */

/** Path-safe name of each GM instrument (0..127). Used to build sample URLs. */
export const GM_INSTRUMENT_NAMES: readonly string[] = Object.freeze([
  "acoustic_grand_piano",      // 0
  "bright_acoustic_piano",     // 1
  "electric_grand_piano",      // 2
  "honkytonk_piano",           // 3
  "electric_piano_1",          // 4
  "electric_piano_2",          // 5
  "harpsichord",               // 6
  "clavinet",                  // 7
  "celesta",                   // 8
  "glockenspiel",              // 9
  "music_box",                 // 10
  "vibraphone",                // 11
  "marimba",                   // 12
  "xylophone",                 // 13
  "tubular_bells",             // 14
  "dulcimer",                  // 15
  "drawbar_organ",             // 16
  "percussive_organ",          // 17
  "rock_organ",                // 18
  "church_organ",              // 19
  "reed_organ",                // 20
  "accordion",                 // 21
  "harmonica",                 // 22
  "tango_accordion",           // 23
  "acoustic_guitar_nylon",     // 24
  "acoustic_guitar_steel",     // 25
  "electric_guitar_jazz",      // 26
  "electric_guitar_clean",     // 27
  "electric_guitar_muted",     // 28
  "overdriven_guitar",         // 29
  "distortion_guitar",         // 30
  "guitar_harmonics",          // 31
  "acoustic_bass",             // 32
  "electric_bass_finger",      // 33
  "electric_bass_pick",        // 34
  "fretless_bass",             // 35
  "slap_bass_1",               // 36
  "slap_bass_2",               // 37
  "synth_bass_1",              // 38
  "synth_bass_2",              // 39
  "violin",                    // 40
  "viola",                     // 41
  "cello",                     // 42
  "contrabass",                // 43
  "tremolo_strings",           // 44
  "pizzicato_strings",         // 45
  "orchestral_harp",           // 46
  "timpani",                   // 47
  "string_ensemble_1",         // 48
  "string_ensemble_2",         // 49
  "synth_strings_1",           // 50
  "synth_strings_2",           // 51
  "choir_aahs",                // 52
  "voice_oohs",                // 53
  "synth_choir",               // 54
  "orchestra_hit",             // 55
  "trumpet",                   // 56
  "trombone",                  // 57
  "tuba",                      // 58
  "muted_trumpet",             // 59
  "french_horn",               // 60
  "brass_section",             // 61
  "synth_brass_1",             // 62
  "synth_brass_2",             // 63
  "soprano_sax",               // 64
  "alto_sax",                  // 65
  "tenor_sax",                 // 66
  "baritone_sax",              // 67
  "oboe",                      // 68
  "english_horn",              // 69
  "bassoon",                   // 70
  "clarinet",                  // 71
  "piccolo",                   // 72
  "flute",                     // 73
  "recorder",                  // 74
  "pan_flute",                 // 75
  "blown_bottle",              // 76
  "shakuhachi",                // 77
  "whistle",                   // 78
  "ocarina",                   // 79
  "lead_1_square",             // 80
  "lead_2_sawtooth",           // 81
  "lead_3_calliope",           // 82
  "lead_4_chiff",              // 83
  "lead_5_charang",            // 84
  "lead_6_voice",              // 85
  "lead_7_fifths",             // 86
  "lead_8_bass__lead",         // 87
  "pad_1_new_age",             // 88
  "pad_2_warm",                // 89
  "pad_3_polysynth",           // 90
  "pad_4_choir",               // 91
  "pad_5_bowed",               // 92
  "pad_6_metallic",            // 93
  "pad_7_halo",                // 94
  "pad_8_sweep",               // 95
  "fx_1_rain",                 // 96
  "fx_2_soundtrack",           // 97
  "fx_3_crystal",              // 98
  "fx_4_atmosphere",           // 99
  "fx_5_brightness",           // 100
  "fx_6_goblins",              // 101
  "fx_7_echoes",               // 102
  "fx_8_scifi",                // 103
  "sitar",                     // 104
  "banjo",                     // 105
  "shamisen",                  // 106
  "koto",                      // 107
  "kalimba",                   // 108
  "bagpipe",                   // 109
  "fiddle",                    // 110
  "shanai",                    // 111
  "tinkle_bell",               // 112
  "agogo",                     // 113
  "steel_drums",               // 114
  "woodblock",                 // 115
  "taiko_drum",                // 116
  "melodic_tom",               // 117
  "synth_drum",                // 118
  "reverse_cymbal",            // 119
  "guitar_fret_noise",         // 120
  "breath_noise",              // 121
  "seashore",                  // 122
  "bird_tweet",                // 123
  "telephone_ring",            // 124
  "helicopter",                // 125
  "applause",                  // 126
  "gunshot",                   // 127
]);

/** Bank name for the drum kit (MIDI channel 10). */
export const GM_DRUMS_NAME = "percussion";

/**
 * French labels for the most common GM programs encountered in piano-centric
 * MIDI files. Sourced from `docs/spec/midi-quality-feedback.md` §6.6.
 * Programs not listed here fall back to a generic `Piste {index}` label
 * (see `gmProgramLabel`).
 */
export const GM_INSTRUMENT_LABELS_FR: Readonly<Record<number, string>> = Object.freeze({
  0: "Piano acoustique",
  1: "Piano clair",
  2: "Piano électrique",
  3: "Piano honky-tonk",
  4: "Piano électrique (Rhodes)",
  5: "Piano électrique (FM)",
  6: "Clavecin",
  7: "Clavinet",
  24: "Guitare classique",
  32: "Contrebasse",
  33: "Basse électrique",
  34: "Basse électrique (médiator)",
  35: "Basse fretless",
  40: "Violon",
  42: "Violoncelle",
  48: "Cordes",
  50: "Cordes synthé",
  52: "Chœur",
  53: "Voix",
  56: "Trompette",
});

/** Drum-kit French label (used when `isDrumChannel === true`). */
export const GM_DRUMS_LABEL_FR = "Batterie";

/**
 * Resolves a human-friendly French label for a track.
 *
 * Resolution order:
 *  1. `isDrum === true` → `Batterie`.
 *  2. Program present in {@link GM_INSTRUMENT_LABELS_FR} → its FR label.
 *  3. Fallback: `Piste {trackIndex + 1}` if `trackIndex` is provided,
 *     otherwise `Piste ?`.
 *
 * `trackIndex` is 0-based (matches `InspectResult.tracks[].index`); the
 * fallback presents it 1-based to the user.
 *
 * @param program GM program 0..127. Ignored when `isDrum === true`.
 * @param trackIndex 0-based track index for the fallback label.
 * @param isDrum Whether the track is on the drum channel.
 */
export function gmProgramLabel(
  program: number,
  trackIndex?: number,
  isDrum = false,
): string {
  if (isDrum) {
    return GM_DRUMS_LABEL_FR;
  }

  const label = GM_INSTRUMENT_LABELS_FR[program];
  if (label !== undefined) {
    return label;
  }

  if (trackIndex !== undefined) {
    return `Piste ${trackIndex + 1}`;
  }

  return "Piste ?";
}
